/**
 * Language carried in the URL.
 *
 * English is the default and keeps the bare address (/works).
 * Every other language owns a prefix: /pt/works, /es/works.
 *
 * The router strips the prefix before matching (rewrite.input) and puts it
 * back on every address it writes (rewrite.output), so the route tree never
 * needs a language segment of its own.
 */

/** Languages with hand-written copy in the code. */
export type Lang = "en" | "pt" | "es";
/** Every language the site serves; catalog-only languages (de) render from English copy. */
/** Languages that so far translate only the home page; other pages show English. */
export const HOME_ONLY_LANGS = ["de", "fr", "ja", "he", "ar", "zh", "hi", "ru", "sw", "yo", "ha", "ig", "pcm", "mr", "vi", "ta", "te", "tr", "is", "sv", "it", "pl", "ro", "nl"] as const;
export type HomeOnlyLang = (typeof HOME_ONLY_LANGS)[number];
export type SiteLang = Lang | HomeOnlyLang;
/** Written right to left. */
export const RTL_LANGS: readonly string[] = ["he", "ar"];
/** The copy a site language reads from before catalog translation. */
export const contentLang = (lang: SiteLang): Lang => (lang === "pt" || lang === "es" ? lang : "en");

/** Languages that appear in the address. English is the unprefix default. */
export const PREFIXED_LANGS = ["pt", "es", ...HOME_ONLY_LANGS] as const;

/** Languages announced to search engines; catalog languages join once translated. */
export const PUBLISHED_LANGS = ["pt", "es"] as const;

export type PrefixedLang = (typeof PREFIXED_LANGS)[number];

const PREFIX_PATTERN = /^\/(pt|es|de|fr|ja|he|ar|zh|hi|ru|sw|yo|ha|ig|pcm|mr|vi|ta|te|tr|is|sv|it|pl|ro|nl)(?=\/|$)/;

/** Split a real browser path into the path the router matches and its language. */
export function splitLangPath(pathname: string): { pathname: string; lang: SiteLang | null } {
  const match = PREFIX_PATTERN.exec(pathname);
  if (!match) return { pathname, lang: null };
  return { pathname: pathname.slice(match[0].length) || "/", lang: match[1] as SiteLang };
}

/** Add a language prefix to a path the router matches. English stays as is. */
export function withLang(pathname: string, lang: SiteLang): string {
  if (!(PREFIXED_LANGS as readonly string[]).includes(lang)) return pathname;
  const base = pathname === "/" ? "" : pathname;
  return `/${lang}${base}`;
}

export const SITE_ORIGIN = "https://www.luizajesusdoprado.com";

/**
 * hreflang alternates for one page, so the Google index of each language
 * points at its own address instead of guessing from a single URL.
 * Pass the path the router matches (no language prefix), e.g. "/works".
 */
export function langAlternates(pathname: string) {
  const alternates: Array<{ rel: "alternate"; hreflang: string; href: string }> = [
    { rel: "alternate", hreflang: "en", href: `${SITE_ORIGIN}${pathname}` },
    ...PUBLISHED_LANGS.map((lang) => ({
      rel: "alternate" as const,
      hreflang: lang as string,
      href: `${SITE_ORIGIN}${withLang(pathname, lang)}`,
    })),
    { rel: "alternate", hreflang: "x-default", href: `${SITE_ORIGIN}${pathname}` },
  ];
  return alternates;
}

/** The full address of a page in one language, for og:url and similar. */
export function pageUrl(pathname: string, lang: SiteLang) {
  return `${SITE_ORIGIN}${withLang(pathname, lang)}`;
}

/**
 * Canonical plus hreflang for one page, aimed at the address of the language
 * being served: each version points at itself and names its siblings.
 */
export function langLinks(pathname: string, lang: SiteLang) {
  // Home-only languages show English beyond the home page, so those copies point at English.
  const own = lang === "en" || (PUBLISHED_LANGS as readonly string[]).includes(lang) || pathname === "/";
  return [
    { rel: "canonical" as const, href: `${SITE_ORIGIN}${withLang(pathname, own ? lang : "en")}` },
    ...langAlternates(pathname),
  ];
}
